import "./tournament-sponsorship-tiers.scss";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router-dom";
import {
  defaultLayouts,
  GSActionBar,
  GSItemList,
  GSSidePanelPage,
} from "golfstatus_react_components";
import { faChevronLeft, faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  clearNotifications,
  getTournamentSponsorshipTiers,
  saveTournamentSponsorshipTier,
  selectLoading,
  selectSponsorshipTierList,
  selectSponsorshipTierNotifications,
  setSponsorshipTierList,
} from "../../../reducers/sponsorshipTierSlice";
import { useEffect } from "react";
import TournamentSponsorshipTierItem from "./tournament-sponsorship-tier-item";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import { getTournamentSponsors } from "../../../reducers/sponsorSlice";
import { selectTiersAvailable } from "../../../reducers/tournamentSlice";
import { getTierPaidMessage } from "../../../forms/SponsorshipTierForm";

const TournamentSponsorshipTiers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch("/:tournamentID/sponsors/sponsorship-tiers");

  const tiersAvailable = useSelector(selectTiersAvailable);
  const tournamentSponsorshipTiers = useSelector(selectSponsorshipTierList);
  const tierLoading = useSelector(selectLoading);
  const sponsorshipTierNotifications = useSelector(
    selectSponsorshipTierNotifications
  );

  const techTier = tournamentSponsorshipTiers?.filter?.(
    (t) => t?.sponsorshipTierType === "technology"
  );
  const customTiers = tournamentSponsorshipTiers?.filter?.(
    (t) => t?.sponsorshipTierType === "custom"
  );
  const defaultTier = tournamentSponsorshipTiers?.filter?.(
    (t) => t?.sponsorshipTierType === "default"
  );

  useEffect(() => {
    dispatch(getTournamentSponsorshipTiers(match?.params?.tournamentID));
  }, [dispatch, match?.params?.tournamentID]);

  const leftNavigation = () => {
    dispatch(getTournamentSponsors(match?.params?.tournamentID));
    navigate("../");
  };

  const getNavigation = () => {
    return {
      title: `Sponsor Tiers`,
      leftIcon: faChevronLeft,
      leftButtonClick: leftNavigation,
    };
  };

  const editTier = (sponsorshipTier) => {
    dispatch(clearNotifications());
    navigate(`./${sponsorshipTier.id}/edit`);
  };

  const CUSTOM_TIER_LIST = "custom-tier-list";

  const sponsorshipTierMoved = (
    sourceItem,
    sourceIndex,
    destItem,
    destIndex,
    destListId
  ) => {
    if (destListId !== CUSTOM_TIER_LIST) {
      return;
    }

    const tiers = [...customTiers];

    const [movedTier] = tiers.splice(sourceIndex, 1);
    tiers.splice(destIndex, 0, movedTier);

    let customIndex = 1;
    const reorderedTiers = tournamentSponsorshipTiers.map((tier) => {
      if (tier.sponsorshipTierType === "custom") {
        return { ...tiers[customIndex - 1], sortOrder: customIndex++ };
      } else {
        return tier;
      }
    });

    dispatch(setSponsorshipTierList(reorderedTiers));

    reorderedTiers.forEach((tier) => {
      if (tier.sponsorshipTierType === "custom") {
        const originalTier = tournamentSponsorshipTiers.find(
          (t) => t.id === tier.id
        );
        if (originalTier && originalTier.sortOrder !== tier.sortOrder) {
          dispatch(saveTournamentSponsorshipTier({ ...tier }));
        }
      }
    });
  };

  const addTier = () => {
    dispatch(clearNotifications());
    navigate(`./add`);
  };

  const getPageActions = () => {
    let actions = [];

    actions.push({
      buttonTitle: "Add Tier",
      type: "no-wrap black mobile",
      actionClick: addTier,
      actionIcon: faPlus,
    });

    return actions;
  };

  const getContent = () => {
    if (!tiersAvailable && !tierLoading?.length > 0) {
      return getTierPaidMessage("Sponsor Tiers");
    }
    return (
      <gs-form>
        <GSActionBar
          type="form-header"
          header="Sponsor Tiers"
          pageActions={getPageActions()}
        />
        <GSItemList
          type="vertical selectable scrollable"
          items={techTier}
          itemSelected={editTier}
          listItem={(item) => <TournamentSponsorshipTierItem {...item} />}
        />
        {customTiers?.length > 0 ? (
          <div
            style={{
              ...defaultLayouts.fullWidth,
              ...defaultLayouts.vStack,
            }}
            className="customTierBorder"
          >
            <GSActionBar
              type="grey-header x-large-pad sticky"
              header="Custom Tiers"
            />
            <GSItemList
              listID={CUSTOM_TIER_LIST}
              type="vertical selectable scrollable"
              items={customTiers}
              itemSelected={editTier}
              itemMoved={sponsorshipTierMoved}
              isSortable={true}
              enableStep={false}
              bottomScrollMargin={100}
              topScrollMargin={100}
              listItem={(item) => <TournamentSponsorshipTierItem {...item} />}
            />
          </div>
        ) : null}

        <GSItemList
          type="vertical selectable scrollable"
          items={defaultTier}
          itemSelected={editTier}
          listItem={(item) => <TournamentSponsorshipTierItem {...item} />}
        />
      </gs-form>
    );
  };

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const [bannerNotifications] = useNotificationBanner({
    notifications: sponsorshipTierNotifications,
    timeoutAction,
  });

  return (
    <tournament-sponsorship-tiers>
      <GSSidePanelPage
        loading={tierLoading?.length > 0}
        header={getNavigation()}
        banner={bannerNotifications}
        content={getContent()}
      />
    </tournament-sponsorship-tiers>
  );
};

export default TournamentSponsorshipTiers;
